import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

const SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:'];

export const safeParsedCvUrl = (url: string) => {
  try {
    const { protocol } = new URL(url, window.location.href);
    return SAFE_URL_PROTOCOLS.includes(protocol) ? url : '';
  } catch {
    return '';
  }
};

const REMARK_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [rehypeSanitize];

const MARKDOWN_COMPONENTS = {
  img: () => null,
  a: ({ children, href }: { children?: React.ReactNode; href?: string }) =>
    href ? (
      <a href={href} target="_blank" rel="noopener noreferrer nofollow">
        {children}
      </a>
    ) : (
      <>{children}</>
    ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="parsed-cv-table-scroll">
      <table>{children}</table>
    </div>
  ),
};

const MarkdownSurface = styled.div`
  font-size: 15px;
  line-height: 1.6;
  color: #1f2937;
  word-break: break-word;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 18px 0 8px;
    line-height: 1.3;
    font-weight: 700;
  }

  h1 {
    font-size: 20px;
  }
  h2 {
    font-size: 17px;
  }
  h3,
  h4,
  h5,
  h6 {
    font-size: 15px;
  }

  > *:first-child {
    margin-top: 0;
  }

  p,
  ul,
  ol,
  blockquote {
    margin: 0 0 12px;
  }

  ul,
  ol {
    padding-left: 20px;
  }

  blockquote {
    margin-left: 0;
    padding-left: 12px;
    border-left: 3px solid #e5e7eb;
    color: #4b5563;
  }

  hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 18px 0;
  }

  code {
    font-size: 13px;
    background: #f3f4f6;
    border-radius: 4px;
    padding: 1px 4px;
  }

  pre {
    max-width: 100%;
    overflow-x: auto;
    background: #f3f4f6;
    border-radius: 6px;
    padding: 10px 12px;
  }

  pre code {
    background: none;
    padding: 0;
  }

  .parsed-cv-table-scroll {
    max-width: 100%;
    overflow-x: auto;
    margin: 0 0 12px;
  }

  table {
    border-collapse: collapse;
    font-size: 14px;
  }

  th,
  td {
    border: 1px solid #e5e7eb;
    padding: 6px 8px;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #f9fafb;
    font-weight: 600;
  }
`;

type CandidateAiParsedCvMarkdownProps = {
  markdown: string;
};

export const CandidateAiParsedCvMarkdown: React.FC<CandidateAiParsedCvMarkdownProps> = React.memo(({ markdown }) => (
  <MarkdownSurface data-testid="parsed-cv-markdown">
    <ReactMarkdown
      skipHtml
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={REHYPE_PLUGINS}
      urlTransform={safeParsedCvUrl}
      components={MARKDOWN_COMPONENTS}
    >
      {markdown}
    </ReactMarkdown>
  </MarkdownSurface>
));

CandidateAiParsedCvMarkdown.displayName = 'CandidateAiParsedCvMarkdown';
